import { useEffect, useRef } from "react";

import { useDisclosure } from "@UI/hooks/useDisclosure";

import {
  serviceRequestPathFragment,
  serviceRequestRoutes,
} from "../../config/routes";
import { useDescriptionDirty } from "../Contexts/DescriptionDirty";

/** `service_request/new` — every flow URL contains it, locale prefix or not. */
const FLOW_PATH =
  serviceRequestRoutes[serviceRequestPathFragment.SERVICE_REQUEST_CREATION];

/**
 * Catches a click on any link that would take the visitor out of the creation
 * flow and asks first (RT-4321), via `TaskCreationExitModal`.
 *
 * One capture-phase listener on the document rather than a wrapper per link:
 * the links live in the shared header, in the screens, and in whatever Rails
 * renders around them, and none of them should have to know about the guard.
 *
 * Deliberately narrow. Modifier and non-primary clicks, `target="_blank"`,
 * in-page anchors and links back into the flow all pass straight through, and
 * there is no `beforeunload` — a refresh or a tab close stays silent by
 * product decision, the draft is already saved on every step.
 *
 * Also skipped on the entry screen while it is still empty (`hasStep` false
 * and `isDirtyRef.current` false): every step screen implies a saved draft
 * already exists, but the entry screen does not, and asking before a visitor
 * has typed or attached anything warns about a loss that hasn't happened.
 */
export const useFlowExitGuard = (hasStep: boolean) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isDirtyRef } = useDescriptionDirty();
  const destination = useRef<string>();

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;

      const anchor = (event.target as Element | null)?.closest?.("a");

      if (!anchor || !anchor.getAttribute("href") || anchor.target === "_blank")
        return;

      const href = anchor.getAttribute("href") as string;

      if (href.startsWith("#")) return;

      const { pathname } = new URL(anchor.href, window.location.origin);

      if (pathname.includes(FLOW_PATH)) return;

      if (!hasStep && !isDirtyRef.current) return;

      event.preventDefault();
      event.stopPropagation();
      destination.current = anchor.href;
      onOpen();
    };

    document.addEventListener("click", onClick, true);

    return () => document.removeEventListener("click", onClick, true);
  }, [onOpen, hasStep, isDirtyRef]);

  return {
    isOpen,
    onClose,
    onQuit: () => {
      onClose();
      // A full load, not a router push: the destination is outside the flow
      // and most often a Rails page. No draft save either — every step has
      // already saved it.
      if (destination.current) window.location.assign(destination.current);
    },
  };
};
